import type { ReactElement } from "react";
import { levelOf, type ScopeGroup, type ScopeSelection } from "./scopes";
import styles from "./ScopeTree.module.css";

interface ScopeTreeProps {
  groups: ScopeGroup[];
  selection: ScopeSelection;
  mode: "edit" | "read";
  onChange?: (groupKey: string, levelKey: string) => void;
}

export function ScopeTree({
  groups,
  selection,
  mode,
  onChange,
}: ScopeTreeProps): ReactElement {
  const rows =
    mode === "read"
      ? groups.filter((group) => levelOf(group, selection).key !== "none")
      : groups;

  return (
    <ul className={styles.tree}>
      {rows.map((group) => {
        const level = levelOf(group, selection);
        const editable = mode === "edit" && !group.readOnly;

        return (
          <li className={styles.row} key={group.key}>
            <div className={styles.text}>
              <span className={styles.key}>{group.key}</span>
              <span className={styles.description}>{level.description}</span>
            </div>
            {editable ? (
              <div className={styles.segments} role="group" aria-label={group.key}>
                {group.levels.map((option) => {
                  const selected = option.key === level.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      aria-pressed={selected}
                      className={[
                        styles.segment,
                        selected ? styles.selected : "",
                        selected && option.key === "none" ? styles.denied : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => onChange?.(group.key, option.key)}
                    >
                      {option.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span className={styles.chip}>
                {level.name}
                {mode === "edit" ? " · fixed" : ""}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
